import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, User } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import type { User as SupabaseUser } from "@supabase/supabase-js";

export const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const avatarRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();

  const isHome = location.pathname === "/";
  const LIGHT_BG_ROUTES = [
    "/services",
    "/features",
    "/generate",
    "/ai-video-production-mexico-city",
    "/aviso-privacidad",
    "/terminos-del-servicio",
  ];
  const isLightPage = LIGHT_BG_ROUTES.some(
    (r) => location.pathname === r || location.pathname.startsWith(r + "/")
  );

  // Scroll-aware editorial surface for inner pages
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Home always uses the readable editorial masthead (no transparent-at-top state)
  const surfaceActive = scrolled || isHome;

  // Theme colors based on page background
  const ink = isLightPage ? "#1A1A1A" : "#ffffff";
  const inkMuted = isLightPage ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.5)";
  const inkFaint = isLightPage ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.08)";
  const inkBorder = isLightPage ? "rgba(0,0,0,0.10)" : "rgba(255,255,255,0.12)";

  // ── Auth ──
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchAvatar(session.user.id);
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_, session) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchAvatar(session.user.id);
      else setAvatarUrl(null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const fetchAvatar = async (userId: string) => {
    try {
      const { data } = await supabase.from("playback_profiles").select("avatar_url").eq("id", userId).single();
      if (data?.avatar_url) setAvatarUrl(data.avatar_url);
    } catch {}
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setAvatarOpen(false);
      }
    };
    if (avatarOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [avatarOpen]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  const handleLogout = async () => {
    setAvatarOpen(false);
    await supabase.auth.signOut();
    navigate("/");
  };

  const userInitial = user?.email?.[0]?.toUpperCase() || "?";

  const labels = {
    aiVideo: language === "es" ? "Video IA · CDMX" : "AI Video · CDMX",
    contact: language === "es" ? "Contacto" : "Contact",
    gallery: language === "es" ? "Galería" : "Gallery",
    generate: language === "es" ? "Espacio de Trabajo" : "Studio Workspace",
    home: language === "es" ? "Inicio" : "Home",
    login: language === "es" ? "Iniciar sesión" : "Log in",
    logout: language === "es" ? "Cerrar sesión" : "Log out",
    profile: language === "es" ? "Perfil" : "Profile",
    services: language === "es" ? "Servicios" : "Services",
  };

  /* ── Shared nav link class (editorial) ── */
  const navLink =
    `font-serif text-[10px] tracking-[0.35em] uppercase transition-opacity duration-500 opacity-60 hover:opacity-100`
    + (isLightPage ? " text-[#1A1A1A]" : " text-white");

  // ─── NAV ITEMS (with active-route highlight) ───
  const navData = [
    { to: "/services", label: labels.services },
    { to: "/features", label: labels.generate },
    { to: "/world-cup-content", label: "Fútbol" },
    { to: "/contact", label: labels.contact },
  ];
  const navItems = (
    <>
      {navData.map(({ to, label }) => {
        const isActive = location.pathname === to;
        return (
          <Link
            key={to}
            to={to}
            className={`${navLink} relative ${isActive ? "opacity-100" : ""}`}
            aria-current={isActive ? "page" : undefined}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            {label}
            {isActive && (
              <span className="absolute left-0 right-0 -bottom-[3px] h-[1px]" style={{ background: "#e84393" }} />
            )}
          </Link>
        );
      })}
    </>
  );

  /**
   * ─────────────────────────────────────────────────────────────
   * IMPORTANT FIXES:
   * 1) Remove mix-blend-mode:difference from nav roots (causes EN/ES distortion + avatar blend).
   * 2) Force crisp text rendering on language toggle.
   * 3) Force avatar to mix-blend-normal.
   * ─────────────────────────────────────────────────────────────
   */

  // ─── LANG TOGGLE (crisp + pink underline, no distortion) ───
  const langToggle = (
    <div
      className="flex items-center gap-4 relative isolate"
      style={{
        zIndex: 100,
        mixBlendMode: "normal",
        filter: "none",
        transform: "translateZ(0)",
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        textRendering: "geometricPrecision",
      }}
    >
      {(["en", "es"] as const).map((lang) => {
        const isActive = language === lang;
        return (
          <button
            key={lang}
            type="button"
            onClick={() => setLanguage(lang)}
            className={`relative font-serif text-[10px] tracking-[0.25em] uppercase mix-blend-normal ${isLightPage ? "text-[#1A1A1A]" : "text-white"}`}
            style={{
              opacity: isActive ? 1 : 0.55,
              transition: "opacity 0.25s ease",
              filter: "none",
              textShadow: "none",
              transform: "translateZ(0)",
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
              textRendering: "geometricPrecision",
            }}
            onMouseEnter={(e) => {
              if (!isActive) e.currentTarget.style.opacity = "0.85";
            }}
            onMouseLeave={(e) => {
              if (!isActive) e.currentTarget.style.opacity = "0.55";
            }}
          >
            {lang}
            {isActive && (
              <span className="absolute left-0 right-0 -bottom-[3px] h-[1px]" style={{ background: "#e84393" }} />
            )}
          </button>
        );
      })}
    </div>
  );

  // ─── AUTH SLOT ───
  const authSlot = user ? (
    <div ref={avatarRef} className="relative isolate">
      <button
        type="button"
        onClick={() => setAvatarOpen(!avatarOpen)}
        className={`w-7 h-7 flex items-center justify-center text-[10px] font-medium transition-opacity duration-500 overflow-hidden mix-blend-normal ${isLightPage ? "text-[#1A1A1A]/70 hover:text-[#1A1A1A]" : "text-white/70 hover:text-white"}`}
        style={{
          background: inkFaint,
          border: `1px solid ${inkBorder}`,
          mixBlendMode: "normal",
          filter: "none",
          transform: "translateZ(0)",
        }}
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            className="w-full h-full object-cover mix-blend-normal"
            style={{ mixBlendMode: "normal", filter: "none", transform: "translateZ(0)" }}
          />
        ) : (
          userInitial
        )}
      </button>

      <AnimatePresence>
        {avatarOpen && (
          <motion.div
            initial={{ opacity: 0, y: 4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-40 py-1 overflow-hidden isolate"
            style={{
              background: "rgba(20,20,20,0.9)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              border: "1px solid rgba(255,255,255,0.1)",
              mixBlendMode: "normal",
              filter: "none",
              transform: "translateZ(0)",
            }}
          >
            <Link
              to="/playback/settings"
              onClick={() => setAvatarOpen(false)}
              className="flex items-center gap-2 px-4 py-2 text-[11px] text-white/60 hover:text-white hover:bg-white/5 transition-colors"
            >
              <User className="w-3.5 h-3.5" /> {labels.profile}
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-2 w-full px-4 py-2 text-[11px] text-white/60 hover:text-white hover:bg-white/5 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> {labels.logout}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  ) : (
    <Link
      to="/playback"
      className={`font-serif text-[10px] tracking-[0.35em] uppercase transition-opacity duration-500 opacity-40 hover:opacity-100 relative group ${isLightPage ? "text-[#1A1A1A]" : "text-white"}`}
    >
      {labels.login}
      <span className={`absolute left-0 right-0 -bottom-[2px] h-[1px] scale-x-0 group-hover:scale-x-100 transition-transform duration-400 origin-left ${isLightPage ? "bg-[#1A1A1A]/50" : "bg-white/50"}`} />
    </Link>
  );

  // ─── EDITORIAL MASTHEAD (all routes) ───
  return (
    <>
      <motion.nav
        key="inner-nav"
        data-navbar-root
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 left-0 right-0 z-[100] isolate"
        style={{
          mixBlendMode: "normal", // ✅ remove difference to prevent distortion
          filter: "none",
          transform: "translateZ(0)",
          background: surfaceActive
            ? isLightPage
              ? "rgba(255,255,255,0.78)"
              : "rgba(10,10,10,0.6)"
            : "transparent",
          backdropFilter: surfaceActive ? "blur(14px) saturate(120%)" : "none",
          WebkitBackdropFilter: surfaceActive ? "blur(14px) saturate(120%)" : "none",
          borderBottom: surfaceActive
            ? isLightPage
              ? "1px solid rgba(0,0,0,0.08)"
              : "1px solid rgba(255,255,255,0.10)"
            : "1px solid transparent",
          transition:
            "background 220ms ease, backdrop-filter 220ms ease, border-color 220ms ease",
        }}
      >
        <div className="container mx-auto px-6 py-5 flex items-center justify-between gap-8">
          <Link
            to="/"
            className={`font-serif text-[11px] tracking-[0.35em] uppercase transition-opacity duration-500 opacity-70 hover:opacity-100 ${isLightPage ? "text-[#1A1A1A]" : "text-white"}`}
          >
            ALABAMA <span className="italic opacity-50">Ai</span>
          </Link>

          <div className="hidden lg:flex items-center gap-12">{navItems}</div>

          <div className="hidden lg:flex items-center gap-6">
            {langToggle}
            {authSlot}
          </div>

          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(true)}
            className={`lg:hidden opacity-70 hover:opacity-100 transition-opacity duration-500 ${isLightPage ? "text-[#1A1A1A]" : "text-white"}`}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </motion.nav>

      {/* Mobile drawer */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100]"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="fixed left-0 top-0 h-full w-80 max-w-[85vw] bg-black border-r border-white/10 z-[100] p-8 overflow-y-auto isolate"
            >
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(false)}
                className="absolute top-6 right-6 text-white/40 hover:text-white transition-opacity duration-500 p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="flex flex-col gap-8 mt-20">
                <Link
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="font-serif text-[11px] tracking-[0.35em] uppercase text-white/50 hover:text-white transition-opacity duration-500 min-h-[44px] flex items-center"
                >
                  {labels.home}
                </Link>
                <Link
                  to="/services"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="font-serif text-[11px] tracking-[0.35em] uppercase text-white/50 hover:text-white transition-opacity duration-500 min-h-[44px] flex items-center"
                >
                  {labels.services}
                </Link>
                <Link
                  to="/features"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="font-serif text-[11px] tracking-[0.35em] uppercase text-white/50 hover:text-white transition-opacity duration-500 min-h-[44px] flex items-center"
                >
                  {labels.generate}
                </Link>
                <Link
                  to="/world-cup-content"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="font-serif text-[11px] tracking-[0.35em] uppercase text-white/50 hover:text-white transition-opacity duration-500 min-h-[44px] flex items-center"
                >
                  Fútbol
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="font-serif text-[11px] tracking-[0.35em] uppercase text-white/50 hover:text-white transition-opacity duration-500 min-h-[44px] flex items-center"
                >
                  {labels.contact}
                </Link>

                {langToggle}

                {user ? (
                  <button
                    type="button"
                    onClick={async () => {
                      setIsMobileMenuOpen(false);
                      await supabase.auth.signOut();
                      navigate("/");
                    }}
                    className="font-serif text-[10px] tracking-[0.25em] uppercase text-white/40 hover:text-white transition-opacity duration-500 min-h-[44px] flex items-center"
                  >
                    {labels.logout}
                  </button>
                ) : (
                  <Link
                    to="/playback"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="font-serif text-[10px] tracking-[0.25em] uppercase text-white/40 hover:text-white transition-opacity duration-500 min-h-[44px] flex items-center"
                  >
                    {labels.login}
                  </Link>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
